import api, { clearAuthAndRedirect } from './api';
import { useAuthTokenStore } from '../stores/authToken';
import type { User } from '../types';

export interface LoginRequest {
  email: string;
  password: string;
  mfaCode?: string;
  remember_me?: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;   // v2.5.0: always empty from server; retained for one release cycle
  token_type: string;
  mfa_required?: boolean;
  mfa_token?: string;
}

export interface SSOProvider {
  id: string;
  provider_type: string;
  name: string;
}

export interface SSOLoginResponse {
  authorization_url: string;
  state: string;
}

/**
 * Decode JWT token payload (without verification)
 */
function decodeJwtPayload(token: string): { exp?: number; [key: string]: unknown } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if expired or invalid, false if still valid
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  // Add 10 second buffer to avoid edge cases
  const expiresAt = payload.exp * 1000;
  return Date.now() >= expiresAt - 10000;
}

/**
 * Get token expiry time in milliseconds
 */
function getTokenExpiryTime(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return null;
  return payload.exp * 1000;
}

/**
 * v2.5.0 (Ship 11, M-3) — clear the access-token store AND sweep any
 * legacy localStorage/sessionStorage residue from pre-Ship-11 code.
 * The one-release sweep can be removed once every deployed frontend is
 * known to be on cookie-only.
 */
function clearAllTokens(): void {
  try {
    useAuthTokenStore().clear();
  } catch {
    // Pinia not initialized yet (rare — bootstrap error). Non-fatal.
  }
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth_storage_type');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('auth_storage_type');
}

/**
 * v2.5.0 (Ship 11, M-3) — single-flight promise lock. Concurrent callers
 * awaiting a token during expiry all await the same in-flight refresh
 * instead of stampeding N ``/refresh`` calls at auth-service.
 */
let _inflightRefresh: Promise<string | null> | null = null;

async function _doRefresh(): Promise<string | null> {
  try {
    // Empty body — the refresh token rides on the httpOnly cookie.
    // axios instance is configured with withCredentials: true so the
    // browser sends the cookie automatically.
    const response = await api.post<TokenResponse>('/api/v1/auth/refresh', {});
    const newAccessToken = response.data.access_token;
    if (!newAccessToken) return null;
    useAuthTokenStore().set(newAccessToken, getTokenExpiryTime(newAccessToken));
    return newAccessToken;
  } catch {
    return null;
  }
}

/**
 * v2.5.0 (Ship 11, M-3) — single-flight refresh helper.
 *
 * If a valid access token is already in the store, returns it without
 * calling the server. Otherwise fires (or joins) a single in-flight
 * ``/refresh`` and returns the resulting access token, or null on failure.
 *
 * ``force: true`` bypasses the cache and always calls the server.
 */
async function ensureFreshAccessToken(opts?: { force?: boolean }): Promise<string | null> {
  const force = opts?.force === true;

  if (!force) {
    try {
      const cached = useAuthTokenStore().accessToken;
      if (cached && !isTokenExpired(cached)) return cached;
    } catch {
      // Pinia not initialized yet — fall through to refresh.
    }
  }

  if (_inflightRefresh) return _inflightRefresh;

  _inflightRefresh = _doRefresh().finally(() => {
    _inflightRefresh = null;
  });
  return _inflightRefresh;
}

export const authService = {
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/api/v1/auth/login', credentials);
    return response.data;
  },

  async logout(): Promise<void> {
    // Always clear tokens first, regardless of API call result
    clearAllTokens();

    // Try to notify backend (optional - don't block on failure)
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Ignore errors - tokens are already cleared
    }
  },

  async verifyMfa(mfaToken: string, code: string, method?: string, trustDevice?: boolean): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/api/v1/auth/mfa/verify', {
      code,
      mfa_token: mfaToken,
      method: method || 'totp',
      trust_device: trustDevice || false,
    });
    return response.data;
  },

  async sendMfaCode(mfaToken: string, method: string): Promise<void> {
    // Send MFA code via email or SMS (for non-TOTP methods)
    await api.post('/api/v1/auth/mfa/send-code', {
      mfa_token: mfaToken,
      method,
    });
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/v1/users/me');
    const data = response.data;
    // Transform snake_case from API to camelCase for frontend
    return {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      userType: data.user_type,
      tenantId: data.tenant_id,
      partnerId: data.partner_id,
      permissions: data.permissions || [],
      // Phase 2.23.c — surface role codes so usePermissions().hasRole()
      // and the isAssistAdmin/isFaxAdmin/etc. flags work. Backend
      // user_to_response() already includes this.
      roles: data.roles || [],
      // v2.4.42 (Ship 3, M-5) — admin-reset gate flag from backend.
      requirePasswordChange: !!data.require_password_change,
    };
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // v2.4.42 (Ship 3, M-5) — silent-refresh so the new access token has
    // require_password_change=False. Otherwise the stale token's claim
    // keeps triggering AuthMiddleware's 403 password_change_required
    // until it expires.
    try {
      await this.refreshAccessToken();
    } catch {
      // Non-fatal — the password change itself succeeded.
    }
  },

  // v2.4.32 — update the current user's own profile via user-service
  // (PUT /api/v1/users/me → UserUpdate). Every portal's Profile Settings
  // view calls this; before v2.4.32 the method didn't exist on
  // authService, so Save Profile threw a TypeError before any HTTP
  // request left the browser.
  async updateProfile(updates: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    timezone?: string;
  }): Promise<User> {
    const response = await api.put('/api/v1/users/me', updates);
    const data = response.data;
    return {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      userType: data.user_type,
      tenantId: data.tenant_id,
      partnerId: data.partner_id,
      permissions: data.permissions || [],
      roles: data.roles || [],
    };
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/api/v1/auth/password/forgot', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/password/reset', {
      token,
      new_password: newPassword,
    });
  },

  /**
   * v2.5.0 (Ship 11, M-3) — store the access token in Pinia; ignore the
   * refresh_token field entirely (auth-service has already Set-Cookie'd
   * the refresh). The ``rememberMe`` hint is a no-op here — cookie
   * Max-Age was decided by the backend based on the login body's
   * remember_me. Kept in the signature for one release to avoid churn
   * on every caller.
   */
  setTokens(tokens: TokenResponse, _rememberMe: boolean = true): void {
    if (!tokens.access_token) return;
    const store = useAuthTokenStore();
    store.set(tokens.access_token, getTokenExpiryTime(tokens.access_token));
  },

  getAccessToken(): string | null {
    try {
      return useAuthTokenStore().accessToken;
    } catch {
      return null;
    }
  },

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    if (isTokenExpired(token)) {
      clearAllTokens();
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      return false;
    }
    return true;
  },

  /**
   * Get remaining session time in milliseconds
   * Returns null if not authenticated or token is invalid
   */
  getSessionTimeRemaining(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;
    const expiryTime = getTokenExpiryTime(token);
    if (!expiryTime) return null;
    const remaining = expiryTime - Date.now();
    return remaining > 0 ? remaining : 0;
  },

  /**
   * v2.5.0 (Ship 11, M-3) — refresh the access token using the httpOnly
   * refresh cookie (sent automatically by the browser via
   * ``withCredentials: true`` on the api instance). Response body's
   * ``refresh_token`` field is empty — the new refresh cookie was set
   * by the response's ``Set-Cookie`` header.
   *
   * Returns true if the access token was refreshed, false otherwise.
   * Preserves the pre-Ship-11 boolean return contract used by
   * ``startSessionExpiryWatcher`` and ``changePassword``.
   */
  async refreshAccessToken(): Promise<boolean> {
    try {
      const newAccessToken = await ensureFreshAccessToken({ force: true });
      return newAccessToken !== null;
    } catch {
      return false;
    }
  },

  /**
   * v2.5.0 (Ship 11, M-3) — single-flight refresh + await. Callers that
   * need a guaranteed-valid access token (WS connect on page reload,
   * axios 401 retry) call this; concurrent invocations share the same
   * in-flight refresh promise.
   *
   * Returns the refreshed access token, or null if refresh failed
   * (invalid cookie, network error).
   */
  async ensureFreshAccessToken(): Promise<string | null> {
    return ensureFreshAccessToken();
  },

  /**
   * Start a session expiry watcher that refreshes token before it expires
   * Returns a cleanup function to stop the watcher
   */
  startSessionExpiryWatcher(): () => void {
    let timeoutId: number | null = null;
    let isRefreshing = false;

    const checkAndSchedule = async () => {
      const remaining = authService.getSessionTimeRemaining();

      if (remaining === null) {
        // Not authenticated
        clearAuthAndRedirect();
        return;
      }

      // Refresh token 2 minutes before expiry
      const REFRESH_BUFFER_MS = 2 * 60 * 1000;

      if (remaining <= REFRESH_BUFFER_MS && !isRefreshing) {
        isRefreshing = true;
        const refreshed = await authService.refreshAccessToken();
        isRefreshing = false;

        if (!refreshed) {
          // Refresh failed, log out
          clearAuthAndRedirect();
          return;
        }
        // Token refreshed, continue watching
      }

      if (remaining <= 0) {
        // Token expired and couldn't refresh
        clearAuthAndRedirect();
        return;
      }

      // Schedule next check - check more frequently as expiry approaches
      const nextCheck = remaining > REFRESH_BUFFER_MS
        ? Math.min(remaining - REFRESH_BUFFER_MS, 60000)  // Check at most every minute
        : 10000; // Check every 10 seconds when close to expiry

      timeoutId = window.setTimeout(checkAndSchedule, nextCheck);
    };

    // Start checking
    checkAndSchedule();

    // Return cleanup function
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  },

  // SSO Methods
  async getSSOProviders(tenantCode: string): Promise<SSOProvider[]> {
    const response = await api.get<{ providers: SSOProvider[] }>(
      `/api/v1/auth/sso/providers?tenant_code=${encodeURIComponent(tenantCode)}`
    );
    return response.data.providers;
  },

  async initiateSSOLogin(
    providerType: string,
    tenantCode: string,
    redirectUri: string
  ): Promise<SSOLoginResponse> {
    const endpoint =
      providerType === 'azure_ad' ? 'azure-ad' : providerType === 'google' ? 'google' : providerType;
    const response = await api.get<SSOLoginResponse>(
      `/api/v1/auth/sso/${endpoint}/login?tenant_code=${encodeURIComponent(tenantCode)}&redirect_uri=${encodeURIComponent(redirectUri)}`
    );
    return response.data;
  },

  async completeSSOLogin(providerType: string, code: string, state: string): Promise<TokenResponse> {
    const endpoint =
      providerType === 'azure_ad' ? 'azure-ad' : providerType === 'google' ? 'google' : providerType;
    const response = await api.post<TokenResponse>(`/api/v1/auth/sso/${endpoint}/callback`, {
      code,
      state,
    });
    return response.data;
  },

  /**
   * Initiate SSO login by provider ID (new hierarchical SSO)
   * This uses the configured SSO provider from the database
   */
  async initiateSSOLoginById(
    providerId: string,
    redirectUri: string
  ): Promise<SSOLoginResponse> {
    const response = await api.get<SSOLoginResponse>(
      `/api/v1/auth/sso/provider/${providerId}/login`,
      { params: { redirect_uri: redirectUri } }
    );
    return response.data;
  },

  /**
   * Complete SSO login by provider ID (new hierarchical SSO)
   */
  async completeSSOLoginById(
    providerId: string,
    code: string,
    state: string
  ): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>(`/api/v1/auth/sso/provider/${providerId}/callback`, {
      code,
      state,
    });
    return response.data;
  },

  // ==================== MFA Methods ====================

  /**
   * Get user's MFA status
   */
  async getMfaStatus(): Promise<MfaStatus> {
    try {
      const response = await api.get<MfaStatus>('/api/v1/auth/mfa/status');
      const data = response.data;
      // Normalize response to support both old and new formats
      return {
        enabled: data.enabled ?? data.mfa_enabled ?? false,
        mfa_enabled: data.enabled ?? data.mfa_enabled ?? false,
        preferred_method: data.preferred_method,
        methods: data.methods || [],
        backup_codes_remaining: data.backup_codes_remaining ?? data.recovery_codes_remaining ?? 0,
        recovery_codes_remaining: data.backup_codes_remaining ?? data.recovery_codes_remaining ?? 0,
        phone_masked: data.phone_masked,
        backup_email: data.backup_email,
      };
    } catch {
      // Return default status if endpoint doesn't exist
      return {
        enabled: false,
        mfa_enabled: false,
        methods: [],
        backup_codes_remaining: 0,
        recovery_codes_remaining: 0,
      };
    }
  },

  /**
   * Initiate MFA setup (supports totp, email, sms)
   */
  async initiateMfaSetup(request: { method: string; phone?: string }): Promise<MfaSetupInitiateResponse> {
    const response = await api.post<MfaSetupInitiateResponse>(
      '/api/v1/auth/mfa/setup/initiate',
      request
    );
    return response.data;
  },

  /**
   * Setup TOTP authenticator app (legacy method)
   */
  async setupTotp(): Promise<{ secret: string; qr_code: string }> {
    const response = await api.post<MfaSetupInitiateResponse>(
      '/api/v1/auth/mfa/setup/initiate',
      { method: 'totp' }
    );
    return {
      secret: response.data.secret || '',
      qr_code: response.data.qr_code || response.data.qr_uri || '',
    };
  },

  /**
   * Verify MFA setup with code
   */
  async verifyMfaSetup(code: string, method: string): Promise<{ success: boolean; backup_codes: string[] }> {
    const response = await api.post<{ success: boolean; backup_codes?: string[] }>(
      '/api/v1/auth/mfa/setup/verify',
      { code, method }
    );
    return {
      success: response.data.success,
      backup_codes: response.data.backup_codes || [],
    };
  },

  /**
   * Verify TOTP setup with code (legacy method)
   */
  async verifyTotpSetup(code: string): Promise<{ success: boolean; backup_codes?: string[] }> {
    return this.verifyMfaSetup(code, 'totp');
  },

  /**
   * Disable MFA entirely (requires password and current code)
   */
  async disableMfa(password: string, code: string): Promise<void> {
    await api.post('/api/v1/auth/mfa/disable', { password, code });
  },

  /**
   * Disable an MFA method (legacy method)
   */
  async disableMfaMethod(methodType: string): Promise<void> {
    await api.post('/api/v1/auth/mfa/disable', { method: methodType });
  },

  /**
   * Regenerate backup codes (requires current MFA code)
   */
  async regenerateBackupCodes(code: string): Promise<{ backup_codes: string[] }> {
    const response = await api.post<{ backup_codes?: string[]; codes?: string[] }>(
      '/api/v1/auth/mfa/recovery-codes',
      { code }
    );
    return { backup_codes: response.data.backup_codes || response.data.codes || [] };
  },

  /**
   * Generate new recovery codes (legacy method)
   */
  async generateRecoveryCodes(): Promise<{ codes: string[] }> {
    const response = await api.post<{ codes: string[]; backup_codes?: string[] }>(
      '/api/v1/auth/mfa/recovery-codes'
    );
    return { codes: response.data.codes || response.data.backup_codes || [] };
  },

  /**
   * List trusted devices
   */
  async listTrustedDevices(): Promise<TrustedDevice[]> {
    const response = await api.get<{ devices: TrustedDevice[] } | TrustedDevice[]>(
      '/api/v1/auth/mfa/trusted-devices'
    );
    // Handle both array and object responses
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.devices || [];
  },

  /**
   * Remove a trusted device
   */
  async removeTrustedDevice(deviceId: string): Promise<void> {
    await api.delete(`/api/v1/auth/mfa/trusted-devices/${deviceId}`);
  },
};

// MFA Status interface for component compatibility
export interface MfaStatus {
  enabled: boolean;
  mfa_enabled?: boolean; // Legacy alias
  preferred_method?: 'totp' | 'email' | 'sms';
  methods: Array<{
    type: 'totp' | 'email' | 'sms';
    enabled: boolean;
    is_primary: boolean;
    verified: boolean;
    last_used?: string;
  }>;
  backup_codes_remaining: number;
  recovery_codes_remaining?: number; // Legacy alias
  phone_masked?: string;
  backup_email?: string;
}

export interface MfaSetupInitiateResponse {
  secret?: string;
  qr_uri?: string;
  qr_code?: string;
  email_masked?: string;
  phone_masked?: string;
}

export interface TrustedDevice {
  id: string;
  device_name: string;
  trusted_until: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export default authService;
